import * as React from "react";
import "./CustomerAddressCtl.scss";
import { CustomerAddress } from "@testout/testout-commerce/models/commerce/CustomerAddress";
import { ContactEmail } from "@testout/testout-commerce/models/ContactEmail";
import { PostalAddress } from "@testout/testout-commerce/models/commerce/PostalAddress";
import PostalAddressCtl from "./PostalAddressCtl";
import EmailCtl from "./input/EmailCtl";
import Api from "./Api";

interface CustomerAddressProps {
    disabled?: boolean;
    hsCompanyId?: number;
    address: CustomerAddress;
    onChange?: (address: CustomerAddress) => void;
    onSetErrorMessage?: (error: string) => void;
    customerNumber?: string;
    className?: string;
    style?: React.CSSProperties;
    noSave?: boolean;
}

interface CustomerAddressState {
    isOpen: boolean;
    items: CustomerAddress[];
    focusIndex: number; 
    openInProgress: boolean;
    saveInProgress: boolean;
    deleteInProgress: boolean;
    error: string;
}


export default class CustomerAddressCtl extends React.PureComponent<CustomerAddressProps, CustomerAddressState> {
    constructor(props: CustomerAddressProps) {
        super(props);
        this.state = {
            isOpen: false, items: [], focusIndex: -1, openInProgress: false, saveInProgress: false, deleteInProgress: false, error: ""
        };
    }

    _nameRef = React.createRef<HTMLDivElement>();
    _valueRef = React.createRef<HTMLInputElement>();
    _scrollerRef = React.createRef<HTMLDivElement>();
    _itemRef = React.createRef<HTMLLIElement>();
    _needFocus: boolean = false;

    onSearchClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (this.props.disabled) return;
        if (this.state.isOpen)
            this.setState({ isOpen: false });
        else 
            this.openItems();
        this._needFocus = true;
    }

    onSaveClick = async (e: React.MouseEvent) => {
        e.stopPropagation();
        this.setState({ saveInProgress: true, error: "" });
        try {
            await Api.put<CustomerAddress>(`/api/customers/${this.props.customerNumber}/addresses`, this.props.address);
            if (this.state.isOpen) this.openItems();
        } catch (error: any) {
            this.setState({ items: [], error: `Save failed. ${error.message}` });
        } finally {
            this.setState({ saveInProgress: false });
        }
        this._needFocus = true;
    }

    onDeleteClick = async (e: React.MouseEvent, i: number) => {
        e.stopPropagation();
        this.setState({ deleteInProgress: true, error: "", focusIndex: i });
        try {
            await Api.delete(`/api/customers/${this.props.customerNumber}/addresses/${encodeURIComponent(this.state.items[i].addressCode)}`);
            const items = [...this.state.items];
            items.splice(i, 1);
            const focusIndex = this.state.focusIndex >= items.length 
                ? items.length - 1 
                : this.state.focusIndex;
            this.setState({ items: items, focusIndex: focusIndex });
        } catch (error: any) {
            this.setState({ items: [], error: `Delete failed. ${error.message}` });
        } finally {
            this.setState({ deleteInProgress: false });
        }
        this._needFocus = true;
    }

    async openItems() {
        this.setState({ isOpen: true, openInProgress: true, focusIndex: -1, items: [], error: "" });
        try {
            const results = await Api.get(`/api/customers/${this.props.customerNumber}/addresses`);
            let items = new Array<CustomerAddress>();
            for (var i = 0; i < results.length; i++) {
                items.push(new CustomerAddress(results[i]));
            }
            const focusIndex = this.getItemIndex(this.props.address.addressCode, results);
            this.setState({ items: items, focusIndex: focusIndex });
        } catch (error: any) {
            this.setState({ items: [], error: `Search failed. ${error.message}` });
        } finally {
            this.setState({ openInProgress: false });
        }
    }

    getItemIndex(addressCode: string, items: CustomerAddress[]|undefined = undefined): number {
        return (items ?? this.state.items).findIndex((item: CustomerAddress) =>
            addressCode && item.addressCode.toUpperCase().includes(addressCode.toUpperCase()));
    }

    onNameKeyDown = (e: React.KeyboardEvent) => {
        if (this.props.disabled) return;
        if (e.key === "Enter") {
            if (!this.state.isOpen)
                this.openItems();
            else if (this.state.focusIndex >= 0)
                this.onItemClick(e, this.state.focusIndex);
            else
                this.setState({ isOpen: false });
            e.preventDefault();
        } else if (e.key === "Escape") {
            if (this.state.isOpen)
                this.setState({ isOpen: false });
            e.preventDefault();
        } else if (e.key === "ArrowDown") {
            if (this.state.isOpen && this.state.focusIndex < this.state.items.length - 1) 
                this.setState({ focusIndex: this.state.focusIndex + 1 });
            e.preventDefault();
        } else if (e.key === "ArrowUp") {
            if (this.state.isOpen && this.state.focusIndex > 0) 
                this.setState({ focusIndex: this.state.focusIndex - 1 });
            e.preventDefault();
        }
    }

    onNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let address = new CustomerAddress(this.props.address);
        address.addressCode = e.target.value;
        const focusIndex = this.state.isOpen
            ? this.getItemIndex(address.addressCode)
            : this.state.focusIndex;
        this.setState({ focusIndex : focusIndex });
        this.props.onChange?.(address);
    }

    onContactChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let address = new CustomerAddress(this.props.address);
        address.contactName = e.target.value;
        this.props.onChange?.(address);
    }

    onCompanyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let address = new CustomerAddress(this.props.address);
        address.companyName = e.target.value;
        this.props.onChange?.(address);
    }

    onPostalChange = (postal: PostalAddress) => {
        let address = new CustomerAddress(this.props.address);
        address.postal = postal;
        this.props.onChange?.(address);
    }

    onPhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let address = new CustomerAddress(this.props.address);
        address.phone = e.target.value;
        this.props.onChange?.(address);
    }

    onEmailChange = (value: string) => {
        let address = new CustomerAddress(this.props.address);
        address.email = value;
        this.props.onChange?.(address);
    }

    lookupEmails = async (value: string): Promise<ContactEmail[] | null> => {
        return await Api.get<ContactEmail[]>(`/api/contacts/emails?search=${value}&companyId=${this.props.hsCompanyId}`) || null;
    }

    onItemClick = (e: React.MouseEvent|React.KeyboardEvent, itemIndex: number) => {
        e.stopPropagation();
        let address = new CustomerAddress(this.state.items[itemIndex]);
        this.setState({ isOpen: false });
        this.props.onChange?.(address);
    }

    onDocumentFocusIn = (e: FocusEvent) => {
        const name = this._nameRef.current;
        if (name && !name.contains(e.target as Node)) {
            if (this.state.isOpen) {
                console.log('lost focus');
                this.setState({ isOpen: false });
                }
        }
    }

    ensureItemVisible() {
        if (this.state.isOpen && this.state.focusIndex >= 0 && this._scrollerRef.current && this._itemRef.current) {
            const liItem = this._itemRef.current as HTMLLIElement;
            const divScroller = this._scrollerRef.current as HTMLDivElement;
            if (liItem.offsetTop < divScroller.scrollTop)
                divScroller.scrollTop = liItem.offsetTop;
            if (liItem.offsetTop + liItem.offsetHeight > divScroller.scrollTop + divScroller.clientHeight)
                divScroller.scrollTop = liItem.offsetTop + liItem.offsetHeight - divScroller.clientHeight;
        }
    }

    componentDidMount() {
        document.addEventListener("focusin", this.onDocumentFocusIn);
        this.ensureItemVisible();
    }

    componentDidUpdate() {
        this.ensureItemVisible();
        if (this._needFocus) {
            this._needFocus = false;
            const inpValue = this._valueRef.current as HTMLInputElement;
            inpValue.focus();
        }
    }

    componentWillUnmount() {
        document.removeEventListener("focusin", this.onDocumentFocusIn);
    }

    render() {
        const { items, focusIndex, isOpen, openInProgress, saveInProgress, deleteInProgress, error } = this.state;
        const { disabled,  address, noSave } = this.props;
        return <div className="address">

                   {!noSave && <div className="name"
                        style={this.props.style} ref={this._nameRef}>

                       <input type="text" ref={this._valueRef}
                              className={`value${disabled ? " disabled" : ""}`}
                              placeholder="name" maxLength={15}
                              value={address.addressCode ?? "PRIMARY"}
                              disabled={disabled}
                              onChange={this.onNameChange}
                              onKeyDown={this.onNameKeyDown}/>
                       <button className={`search${openInProgress ? " spinner" : ""}`}
                               onClick={this.onSearchClick}
                               disabled={disabled || openInProgress}></button>
                       <button className={`save${saveInProgress ? " spinner" : ""}`}
                               onClick={this.onSaveClick}
                               disabled={disabled || saveInProgress}></button>

                       {isOpen &&
                           <div className="scroller" ref={this._scrollerRef}>
                               {error && <div>{error}</div>}
                               <ul>
                                   {items.map((item: CustomerAddress, i) =>
                                       <li key={i} ref={i === focusIndex ? this._itemRef : undefined}
                                           className={i === focusIndex ? "focus" : undefined}
                                           onClick={(e: React.MouseEvent) => this.onItemClick(e, i) }>
                                           {item.addressCode}
                                           <span>{[
                                               item.contactName,
                                               item.companyName,
                                               item.postal?.toString() ?? "",
                                               item.phone,
                                               item.email
                                           ].filter(v => v).join(" ● ")}</span>
                                           <button className={`trash${deleteInProgress ? " spinner" : ""}`}
                                                   onClick={(e: React.MouseEvent) => this.onDeleteClick(e, i)}
                                                   disabled={disabled || deleteInProgress}></button>
                                       </li>)}
                               </ul>
                           </div>}
                   </div>}

                   <input type="text"
                          placeholder="contact"
                          value={address.contactName}
                          disabled={disabled} className={`input${disabled ? " disabled" : ""}`}
                          onChange={this.onContactChange}/>
                   <input type="text"
                          placeholder="company"
                          value={address.companyName ?? ""}
                          disabled={disabled || noSave} className={`input${(disabled || noSave) ? " disabled" : ""}`}
                          onChange={this.onCompanyChange}/>
                   <PostalAddressCtl disabled={disabled}
                                     postal={address.postal ?? { countryCode: "USA" }}
                                     onChange={this.onPostalChange}
                                     defaultCountryCode="USA"/>
                   <input type="tel"
                          placeholder="phone"
                          value={address.phone ?? ""}
                          disabled={disabled}className={`input${disabled ? " disabled" : ""}`}
                          onChange={this.onPhoneChange}/>
                   <EmailCtl placeholder="email"
                             value={address.email}
                             disabled={disabled}
                             onSelect={this.lookupEmails}
                             onChange={this.onEmailChange}
                             onSetErrorMessage={this.props.onSetErrorMessage}/>
               </div>;
    }
}